/**
 * Attaching a broker to a sale — optional, and only when one actually brought the customer in.
 *
 * Unlike customers, the active broker list is small enough to fetch whole and filter locally —
 * brokers are registered by the owner ahead of time (see the dashboard's Brokers page), not
 * created at the counter, so there is no "new broker" path here.
 */

import { useMemo, useState } from 'react';

import { useListBrokersQuery, type Broker } from '@pos/shared';

import { Button, EmptyState, ErrorState, Field, Sheet, SkeletonRows } from '@/shared/ui';

export type BrokerPickerSheetProps = {
  open: boolean;
  onPick: (broker: Broker) => void;
  onDismiss: () => void;
};

export function BrokerPickerSheet({ open, onPick, onDismiss }: BrokerPickerSheetProps) {
  const [term, setTerm] = useState('');

  const { data, isFetching, error, refetch } = useListBrokersQuery(
    { onlyActive: true },
    { skip: !open },
  );

  const brokers = useMemo(() => {
    const needle = term.trim().toLowerCase();
    const all = data ?? [];
    if (needle.length === 0) return all;
    return all.filter((broker) => broker.name.toLowerCase().includes(needle));
  }, [data, term]);

  return (
    <Sheet
      open={open}
      onDismiss={() => {
        setTerm('');
        onDismiss();
      }}
      title="Attach a broker"
    >
      <Field
        label="Search by name"
        placeholder="Type to filter"
        value={term}
        autoFocus
        onChange={(event) => {
          setTerm(event.target.value);
        }}
      />

      <div className="mt-4">
        {isFetching ? (
          <SkeletonRows rows={3} />
        ) : error !== undefined ? (
          <ErrorState
            title="The broker list could not be loaded"
            body="Check the connection and try again."
            onRetry={() => void refetch()}
          />
        ) : brokers.length === 0 ? (
          <EmptyState
            title={term.length > 0 ? 'No match' : 'No active brokers'}
            body={
              term.length > 0
                ? 'Check the spelling, or clear the box to see everyone.'
                : 'Brokers are registered by the owner on the dashboard.'
            }
          />
        ) : (
          <ul className="flex flex-col gap-2">
            {brokers.map((broker) => (
              <li key={broker.id}>
                <Button
                  variant="quiet"
                  fullWidth
                  className="justify-start"
                  onClick={() => {
                    setTerm('');
                    onPick(broker);
                  }}
                >
                  <span className="flex flex-col items-start">
                    <span className="font-semibold">{broker.name}</span>
                    {broker.phone !== null && broker.phone !== undefined && (
                      <span className="text-xs text-content-muted">{broker.phone}</span>
                    )}
                  </span>
                </Button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </Sheet>
  );
}
